import { useEffect, useState } from 'react';

// A previous account's cached plan must never authorize or redirect this account.
export const useAdminStorePlan = user => {
  const userId = user?.id;
  const role = user?.role;
  const key = `${userId || ''}:${role || ''}`;
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ key: '', status: 'loading', plan: 'none' });

  useEffect(() => {
    if (!userId || role === 'hyperadmin') return;
    const controller = new AbortController();
    let cancelled = false;
    const timeout = setTimeout(() => controller.abort(), 8000);
    setState({ key, status: 'loading', plan: 'none' });
    (async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v2/subscriptions`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          signal: controller.signal, cache: 'no-store',
        });
        if (!response.ok) throw new Error('Store access could not be verified');
        const subscriptions = await response.json();
        if (!Array.isArray(subscriptions)) throw new Error('Invalid store access response');
        const plans = subscriptions.map(s => s.plan);
        const plan = ['diamond', 'premium', 'standard'].find(p => plans.includes(p)) || 'none';
        if (!cancelled) setState({ key, status: 'ready', plan });
      } catch {
        // Cleanup aborts are ignored; timeouts/network errors offer a retry, never "none".
        if (!cancelled) setState({ key, status: 'error', plan: 'none' });
      } finally {
        clearTimeout(timeout);
      }
    })();
    return () => { cancelled = true; clearTimeout(timeout); controller.abort(); };
  }, [userId, role, key, attempt]);

  const current = role === 'hyperadmin' ? { status: 'ready', plan: 'diamond' }
    : state.key === key ? state : { status: 'loading', plan: 'none' };
  return { storePlan: current.plan, planLoaded: current.status === 'ready',
    planError: current.status === 'error', retryPlan: () => setAttempt(n => n + 1) };
};